import { useState } from "react";

import { Button } from "components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "components/ui/dialog";
import { Input } from "components/ui/input";
import {
  IStoreDeposit,
  IUpdateDepositNsuResult,
} from "src/@types/store-deposit-maintenance";

import {
  extractNsuPrefix,
  formatAmount,
  formatOperationDate,
  text,
} from "../deposit-formatters";
import {
  DEPOSIT_MAINTENANCE_LABELS,
  NSU_CONFIRMATION_FALLBACK,
  NSU_LENGTH_HINT,
  NSU_LENGTH_PLACEHOLDER,
  NSU_MAX_LENGTH,
} from "../types";

interface UpdateNsuModalProps {
  readonly deposit: IStoreDeposit | null;
  readonly nsuLength: number | undefined;
  readonly isSaving: boolean;
  readonly confirmation: IUpdateDepositNsuResult | null;
  readonly onClose: () => void;
  readonly onSubmit: (nsu: string) => void;
}

const hasValidLength = (nsu: string, nsuLength: number | undefined): boolean => {
  if (nsuLength === undefined || nsuLength === 0) return true;
  return extractNsuPrefix(nsu).trim().length === nsuLength;
};

type NsuModalStep = "input" | "confirmation";

const STEP_TITLE: Record<NsuModalStep, string> = {
  input: DEPOSIT_MAINTENANCE_LABELS.editNsuTitle,
  confirmation: DEPOSIT_MAINTENANCE_LABELS.confirmNsuTitle,
};

const STEP_DESCRIPTION: Record<NsuModalStep, string> = {
  input: DEPOSIT_MAINTENANCE_LABELS.editNsuDescription,
  confirmation: DEPOSIT_MAINTENANCE_LABELS.confirmNsuDescription,
};

export function UpdateNsuModal(props: UpdateNsuModalProps) {
  const [nsu, setNsu] = useState<string>(props.deposit?.nsu ?? "");

  if (props.deposit === null) return null;

  const isConfirmationStep: boolean = props.confirmation !== null;
  const step: NsuModalStep = isConfirmationStep ? "confirmation" : "input";
  const suggestedNsu: string = props.confirmation?.suggestedNsu ?? "";
  const canSubmit: boolean =
    nsu.trim() !== "" && hasValidLength(nsu, props.nsuLength);

  const handleConfirm = (): void => {
    if (isConfirmationStep) {
      props.onSubmit(suggestedNsu);
      return;
    }
    props.onSubmit(nsu.trim());
  };

  const lengthHint: string = NSU_LENGTH_HINT.replace(
    NSU_LENGTH_PLACEHOLDER,
    String(props.nsuLength),
  );

  return (
    <Dialog open onOpenChange={props.onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{STEP_TITLE[step]}</DialogTitle>
          <DialogDescription>{STEP_DESCRIPTION[step]}</DialogDescription>
        </DialogHeader>

        {isConfirmationStep ? (
          <p
            data-testid="nsu-confirmation-message"
            className="whitespace-pre-line text-sm text-slate-700"
          >
            {props.confirmation?.message ?? NSU_CONFIRMATION_FALLBACK}
          </p>
        ) : (
          <div className="flex flex-col gap-3">
            <dl className="grid grid-cols-2 gap-2 text-xs text-slate-600">
              <div>
                <dt className="text-slate-500">
                  {DEPOSIT_MAINTENANCE_LABELS.depositDateLabel}
                </dt>
                <dd>{formatOperationDate(props.deposit.operationDate)}</dd>
              </div>
              <div>
                <dt className="text-slate-500">
                  {DEPOSIT_MAINTENANCE_LABELS.depositAmountLabel}
                </dt>
                <dd>{formatAmount(props.deposit.amount)}</dd>
              </div>
              <div className="col-span-2">
                <dt className="text-slate-500">
                  {DEPOSIT_MAINTENANCE_LABELS.currentAccountLabel}
                </dt>
                <dd className="font-mono">{text(props.deposit.complement)}</dd>
              </div>
            </dl>

            <div>
              <label
                className="text-xs text-slate-500 mb-1 block"
                htmlFor="deposit-nsu-input"
              >
                {DEPOSIT_MAINTENANCE_LABELS.nsuLabel}
              </label>
              <Input
                id="deposit-nsu-input"
                value={nsu}
                onChange={(event) => setNsu(event.target.value)}
                maxLength={NSU_MAX_LENGTH}
                className="h-8 font-mono"
              />
              {props.nsuLength !== undefined && props.nsuLength > 0 && (
                <p className="text-xs text-slate-500 mt-1">{lengthHint}</p>
              )}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={props.onClose}>
            {DEPOSIT_MAINTENANCE_LABELS.cancelButton}
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={props.isSaving || (!isConfirmationStep && !canSubmit)}
            className="bg-slate-800 hover:bg-slate-900 text-white"
          >
            {props.isSaving
              ? DEPOSIT_MAINTENANCE_LABELS.savingButton
              : DEPOSIT_MAINTENANCE_LABELS.confirmButton}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
